import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ArrowLeft, Rocket, RotateCw } from 'lucide-react';
import useAutoSave from '../../hooks/useAutoSave';
import * as SignService from '../../service/sign';
import { reportColumnCopy } from '../columnCopy';
import { validateTemplate, maxReachableStep, stepHint } from './validation';
import { MAX_SIGNERS } from './sign-overlays';
import Step1Documents from './Step1Documents';
import Step2MapFields from './Step2MapFields';
import StepNav from './StepNav';
import FooterHint from './FooterHint';

const STEPS = [
  { n: 1, label: 'Documents' },
  { n: 2, label: 'Map fields' },
];
const LAST_STEP = STEPS.length;
const MAX_DOCUMENTS = 10;

/**
 * The template editor — two steps, the same count as the envelope builder now.
 *
 * They arrived there differently and still differ inside. A template holds only
 * what a template alone can know: the documents, where the fields sit, and who
 * the parties are as unfilled ROLES. It has no wording, no destination, no
 * signing order and no deadline, because each of those describes one act of
 * sending — so it never had a third step. The builder lost its own when four of
 * that step's five cards became board settings and the fifth moved into the
 * properties panel.
 *
 * `mode='template'` is what keeps the shared step 2 honest about the difference:
 * a role has no address to set and a template has no request to configure, so
 * neither of the two new panel states applies here.
 *
 * Roles are therefore added, named and removed entirely in step 2's palette,
 * beside the fields they own. A role you cannot see while placing its signature
 * box is a role nobody remembers to map.
 *
 * A template also cannot copy another template in (that would blur which one a
 * signature came from), and its final action is Publish rather than Send.
 * Publishing writes the immutable snapshot envelopes copy from, and sits in the
 * footer on the LAST STEP only — the same corner, and the same "one
 * irreversible act at the end" shape, as the builder's Send for signature.
 */
export default function TemplateEditor({
  monday,
  getSessionToken,
  templateId,
  boardId,
  itemId,
  board,
  onClose,
  /** Called after a successful publish — see handlePublish. */
  onPublished,
}) {
  const [template, setTemplate] = useState(null);
  const [step, setStep] = useState(1);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [itemFiles, setItemFiles] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);

  const metaRef = useRef(null);
  const fieldsRef = useRef(null);
  /** Per-document debounce timers for inline renames. */
  const renameTimers = useRef({});

  const { autoSave: autoSaveMeta, formSaving: metaSaving } = useAutoSave({
    saveFn: async (token, payload) => {
      await SignService.updateTemplate(token, templateId, payload);
    },
    getSessionToken,
    formRef: metaRef,
    delay: 700,
  });
  const { autoSave: autoSaveFields, formSaving: fieldsSaving } = useAutoSave({
    saveFn: async (token, payload) => {
      await SignService.saveTemplateFields(token, templateId, payload);
    },
    getSessionToken,
    formRef: fieldsRef,
    delay: 700,
  });

  const load = useCallback(async () => {
    try {
      const token = await getSessionToken();
      const res = await SignService.getTemplate(token, templateId);
      if (res?.ok) setTemplate(res.template);
      else setLoadError(true);
    } catch (err) {
      console.error('Failed to load sign template', err);
      setLoadError(true);
    }
  }, [getSessionToken, templateId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    getSessionToken().then((t) => {
      if (!cancelled) setSessionToken(t);
    });
    return () => {
      cancelled = true;
    };
  }, [getSessionToken]);

  const patchMeta = useCallback(
    (patch) => {
      setTemplate((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          ...patch,
          // Any edit past a publish means the working copy has moved on.
          hasUnpublishedChanges: prev.version > 0 ? true : prev.hasUnpublishedChanges,
        };
        // name and roles are the whole editable surface — see the PUT's
        // accepted body in sign-template.controller.ts.
        metaRef.current = { name: next.name, roles: next.roles };
        return next;
      });
      autoSaveMeta();
    },
    [autoSaveMeta],
  );

  const patchFields = useCallback(
    ({ prefillFields, signerFields }) => {
      setTemplate((prev) =>
        prev
          ? {
              ...prev,
              prefillFields,
              signerFields,
              needsReview: false,
              hasUnpublishedChanges: prev.version > 0 ? true : prev.hasUnpublishedChanges,
            }
          : prev,
      );
      fieldsRef.current = { prefillFields, signerFields };
      autoSaveFields();
    },
    [autoSaveFields],
  );

  // ── documents ──────────────────────────────────────────────────────────────

  const withBusy = async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
    }
  };

  const notifyFailure = (res, fallback) =>
    monday.execute('notice', { message: res?.message || fallback, type: 'error', timeout: 5000 });

  const handleUpload = (file) =>
    withBusy(async () => {
      const token = await getSessionToken();
      const res = await SignService.uploadTemplateDocument(token, templateId, file);
      if (res?.ok) setTemplate((prev) => ({ ...res.template, hasUnpublishedChanges: prev?.version > 0 }));
      else notifyFailure(res, 'Could not add that PDF.');
    });

  const handleAddFromColumn = (columnId, columnTitle) =>
    withBusy(async () => {
      const token = await getSessionToken();
      const res = await SignService.addTemplateDocumentFromColumn(
        token,
        templateId,
        columnId,
        columnTitle,
        itemId,
      );
      if (!res?.ok) {
        notifyFailure(res, 'Could not add those files.');
        return;
      }
      setTemplate((prev) => ({ ...res.template, hasUnpublishedChanges: prev?.version > 0 }));
      reportColumnCopy(monday, res, columnTitle);
    });

  const handleRemoveDocument = async (documentId) => {
    const doc = template?.documents?.find((d) => d.id === documentId);
    const fieldCount = [...(template?.prefillFields || []), ...(template?.signerFields || [])].filter(
      (f) => f.documentId === documentId,
    ).length;

    if (fieldCount > 0) {
      const confirmed = await monday
        .execute('confirm', {
          message: `Remove “${doc?.name || 'this document'}”? The ${fieldCount} field${
            fieldCount === 1 ? '' : 's'
          } placed on it will be removed too.`,
          confirmButton: 'Remove',
          cancelButton: 'Cancel',
        })
        .then((res) => res?.data?.confirm)
        .catch(() => window.confirm('Remove this document and its fields?'));
      if (!confirmed) return;
    }

    const token = await getSessionToken();
    const res = await SignService.removeTemplateDocument(token, templateId, documentId);
    if (res?.ok) setTemplate((prev) => ({ ...res.template, hasUnpublishedChanges: prev?.version > 0 }));
    else notifyFailure(res, 'Could not remove that document.');
  };

  const handleReorder = async (order) => {
    setTemplate((prev) =>
      prev
        ? { ...prev, documents: order.map((id) => prev.documents.find((d) => d.id === id)).filter(Boolean) }
        : prev,
    );
    const token = await getSessionToken();
    const res = await SignService.reorderTemplateDocuments(token, templateId, order);
    if (res?.ok) setTemplate((prev) => ({ ...res.template, hasUnpublishedChanges: prev?.version > 0 }));
  };

  /**
   * The item's File columns, each with the PDFs it currently holds.
   *
   * Fetched per column with aliases in ONE round trip: `Asset` carries no
   * column attribution, so a flat `assets` query cannot say which column a
   * file came from — and the column is now the unit the user picks.
   *
   * Counts only; the server re-reads the column when you actually add, because
   * "everything in the column" has to mean everything in it then, not when this
   * tab was opened.
   */
  const loadItemFiles = useCallback(async () => {
    if (itemFiles !== null || !itemId) return;
    // Bail WITHOUT recording a result: the board loads asynchronously, and
    // caching "no columns" here would be permanent — the `itemFiles !== null`
    // guard above stops the retry once the board arrives.
    if (!board) return;
    const columns = board.fileColumns || [];
    if (!columns.length) {
      setItemFiles([]);
      return;
    }
    try {
      const selections = columns
        .map((col, i) => `c${i}: assets (column_ids: ["${col.id}"]) { id name file_extension }`)
        .join(' ');
      const res = await monday.api(
        `query ($ids: [ID!]) { items (ids: $ids) { ${selections} } }`,
        { variables: { ids: [String(itemId)] } },
      );
      const item = res?.data?.items?.[0] || {};
      setItemFiles(
        columns.map((col, i) => ({
          id: col.id,
          title: col.title,
          pdfCount: (item[`c${i}`] || []).filter(
            (a) => String(a.file_extension || '').toLowerCase().replace('.', '') === 'pdf',
          ).length,
        })),
      );
    } catch (err) {
      console.error('Failed to list item file columns', err);
      setItemFiles([]);
    }
  }, [itemFiles, itemId, monday, board]);

  useEffect(() => {
    loadItemFiles();
  }, [loadItemFiles]);

  // ── publish ────────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    if (publishing) return;
    setPublishing(true);
    try {
      const token = await getSessionToken();
      const res = await SignService.publishTemplate(token, templateId);
      if (res?.ok && res.template) {
        setTemplate({ ...res.template, hasUnpublishedChanges: false });
        monday.execute('notice', {
          message: 'Template published — signing requests can now start from it.',
          type: 'success',
          timeout: 5000,
        });
        (res.warnings || []).forEach((w) =>
          monday.execute('notice', { message: w.message || w, type: 'info', timeout: 5000 }),
        );
        // Publishing is the end of the template's job: a template exists to
        // start a request from, and the moment it can be is the moment you are
        // most likely to want one. Leaving the editor open invites another lap
        // of edits on something already published, which only creates an
        // unpublished delta.
        //
        // AFTER the notices, so the success message is queued before the view
        // changes under it.
        if (onPublished) onPublished();
      } else if (res?.errors?.length) {
        // The server validated against a fresh board read — jump to its step.
        const first = res.errors[0];
        monday.execute('notice', {
          message: `Can't publish yet: ${first.message}`,
          type: 'error',
          timeout: 6000,
        });
        setStep(first.step || 2);
      } else {
        notifyFailure(res, 'Publishing failed — please try again.');
      }
    } catch (err) {
      console.error('Publish failed', err);
      monday.execute('notice', { message: 'Publishing failed — please try again.', type: 'error' });
    } finally {
      setPublishing(false);
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div className='flex flex-col items-center justify-center h-full gap-3 bg-gray-50'>
        <div className='text-sm text-gray-600'>This template could not be loaded.</div>
        <button onClick={onClose} className='px-3 py-1.5 rounded-md text-sm font-medium bg-gray-900 text-white'>
          Back to templates
        </button>
      </div>
    );
  }

  if (!template) {
    return (
      <div className='flex items-center justify-center h-full text-sm text-gray-500 bg-gray-50'>
        Loading template…
      </div>
    );
  }

  const reachable = maxReachableStep(template, LAST_STEP);
  const { canPublish } = validateTemplate(template, board);
  const hint = stepHint(template, board, step, 'template');
  const documentUrl = (documentId) =>
    sessionToken ? SignService.getTemplateDocumentUrl(sessionToken, documentId) : null;
  const published = Number(template.version) > 0;

  return (
    <div className='flex flex-col h-full flex-1 min-h-0 overflow-hidden bg-gray-50'>
      <header className='flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-white border-b border-gray-200'>
        <button
          onClick={onClose}
          title='Back to templates'
          className='p-1.5 rounded-md text-gray-500 hover:bg-gray-100 flex-shrink-0'
        >
          <ArrowLeft size={16} />
        </button>
        <div className='min-w-0'>
          <input
            type='text'
            value={template.name}
            maxLength={255}
            onChange={(e) => patchMeta({ name: e.target.value })}
            className='text-sm font-semibold text-gray-900 bg-transparent focus:outline-none focus:bg-gray-50 rounded px-1 -ml-1 w-48'
          />
          <div className='text-xs text-gray-500 leading-tight px-1 -ml-1'>
            Template · {board?.boardName ? `${board.boardName} board` : 'this board'}
          </div>
        </div>

        <div className='flex-1'>
          <StepNav steps={STEPS} step={step} reachable={reachable} onGo={setStep} />
        </div>

        <div className='flex items-center gap-2 flex-shrink-0'>
          <span className='text-xs text-gray-400 w-14 text-right'>
            {metaSaving || fieldsSaving ? (
              <span className='inline-flex items-center gap-1'>
                <RotateCw size={11} className='animate-spin' />
                Saving
              </span>
            ) : (
              'Saved'
            )}
          </span>
          {/*
            "Active v1", not "v1". The version alone answers "which version" but
            not "can a request start from this yet?", which is the thing the
            editor is otherwise silent about — and the only reason publishing
            matters. Paired with "Draft" as its opposite, the chip reads as a
            state rather than a number.
          */}
          <span
            className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
              published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {published ? `Active v${template.version}` : 'Draft'}
          </span>
        </div>
      </header>

      <main className='flex-1 overflow-hidden flex'>
        {step === 1 && (
          <Step1Documents
            documents={template.documents}
            itemFiles={itemFiles}
            busy={busy}
            maxDocuments={MAX_DOCUMENTS}
            // A template copying another template would blur which one a
            // signature came from, so that door is closed here.
            allowTemplates={false}
            scopeNote={`Every signing request on ${
              board?.boardName || 'this board'
            } can start from this template. Signers work through these documents in order.`}
            onUpload={handleUpload}
            onAddFromColumn={handleAddFromColumn}
            onRemove={handleRemoveDocument}
            onReorder={handleReorder}
            onRename={(documentId, name) => {
              setTemplate((prev) =>
                prev
                  ? {
                      ...prev,
                      documents: prev.documents.map((d) => (d.id === documentId ? { ...d, name } : d)),
                    }
                  : prev,
              );
              // Its own debounced call — the meta payload has no documents
              // field, so folding it in there would look saved and vanish.
              clearTimeout(renameTimers.current[documentId]);
              renameTimers.current[documentId] = setTimeout(async () => {
                const token = await getSessionToken();
                await SignService.renameTemplateDocument(token, templateId, documentId, name);
              }, 700);
            }}
          />
        )}
        {step === 2 && (
          <Step2MapFields
            subject={template}
            mode='template'
            board={board}
            documentUrl={documentUrl}
            onFieldsChange={patchFields}
            onAddSigner={() => {
              const roles = template.roles || [];
              if (roles.length >= MAX_SIGNERS) return;
              patchMeta({
                roles: [...roles, { roleLabel: `Signer ${roles.length + 1}`, order: roles.length + 1 }],
              });
            }}
            onRenameSigner={(index, roleLabel) =>
              patchMeta({
                roles: (template.roles || []).map((r, i) => (i === index ? { ...r, roleLabel } : r)),
              })
            }
            onRemoveSigner={async (index) => {
              const roles = template.roles || [];
              if (roles.length <= 1) return;
              const fieldCount = (template.signerFields || []).filter((f) => f.signerIndex === index).length;
              if (fieldCount > 0) {
                const confirmed = await monday
                  .execute('confirm', {
                    message: `Remove ${roles[index]?.roleLabel || 'this role'}? Their ${fieldCount} placed field${
                      fieldCount === 1 ? '' : 's'
                    } will be removed too.`,
                    confirmButton: 'Remove role',
                    cancelButton: 'Cancel',
                  })
                  .then((res) => res?.data?.confirm)
                  .catch(() => window.confirm('Remove this role and its fields?'));
                if (!confirmed) return;
              }
              patchFields({
                prefillFields: template.prefillFields || [],
                signerFields: (template.signerFields || [])
                  .filter((f) => f.signerIndex !== index)
                  .map((f) => (f.signerIndex > index ? { ...f, signerIndex: f.signerIndex - 1 } : f)),
              });
              patchMeta({
                roles: roles.filter((_, i) => i !== index).map((r, i) => ({ ...r, order: i + 1 })),
              });
            }}
          />
        )}
      </main>

      <footer className='flex-shrink-0 flex items-center gap-3 px-4 py-2.5 bg-white border-t border-gray-200'>
        <button
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          className={`px-3 py-1.5 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:border-gray-300 ${
            step === 1 ? 'invisible' : ''
          }`}
        >
          Back
        </button>

        {/* Spacer, then the hint beside the button it explains. */}
        <span className='flex-1' />
        <FooterHint hint={hint} />

        {/*
          Publish lives here, not in the header, and only on the last step. It is
          the counterpart of the builder's "Send for signature": the one
          irreversible act, in the corner the eye ends on, beside the hint that
          explains why it is disabled.
        */}
        {step === LAST_STEP ? (
          <button
            onClick={handlePublish}
            disabled={!canPublish || publishing || (published && !template.hasUnpublishedChanges)}
            title={
              published && !template.hasUnpublishedChanges
                ? 'The published version matches what you see — make a change to update'
                : canPublish
                  ? 'Publish this template'
                  : 'Finish the highlighted steps to publish'
            }
            className='flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
          >
            {publishing ? <RotateCw size={13} className='animate-spin' /> : <Rocket size={13} />}
            {published ? 'Update this template' : 'Publish'}
          </button>
        ) : (
          <button
            onClick={() => setStep((s) => Math.min(reachable, s + 1))}
            disabled={step >= reachable}
            className='px-4 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed'
          >
            Next
          </button>
        )}
      </footer>
    </div>
  );
}
